import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  Coins, Clock, Users, Shield, TrendingUp, Copy, CheckCircle,
  ExternalLink, Wallet, ChevronRight, Lock, Zap
} from 'lucide-react';

const TOKEN_PRICE = 0.68; // $0.68 per share
const TOTAL_SUPPLY = 100000000; // 100M shares
const LAUNCH_DATE = new Date('2026-08-02');

const TokenOffering = () => {
  const { account, isConnected, connectWallet, balance, walletType, getNetworkName, chainId } = useWeb3();
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [copiedAddress, setCopiedAddress] = useState(null);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  // Token sale stats (would come from API in production)
  const [saleStats, setSaleStats] = useState({
    tokensSold: 15750000,
    participants: 2847,
    raisedUSD: 10710000,
  });

  const progress = (saleStats.tokensSold / TOTAL_SUPPLY) * 100;
  const tokensRemaining = TOTAL_SUPPLY - saleStats.tokensSold;

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const difference = LAUNCH_DATE - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  const depositAddresses = [
    { network: 'Bitcoin (BTC)', address: 'bc1qn4nf4jkz8qv95emhezyk6n5x2qgpclxrcg0s4y', icon: '₿', color: 'text-[#F7931A]' },
    { network: 'Ethereum (ETH)', address: '0xb745dc81c09bb00cc2faec09aa9a68dd131bace2', icon: 'Ξ', color: 'text-blue-400' },
    { network: 'Tron (TRX)', address: 'TGky5UyPaZ74j7qgcREpy4kJ8Hs1vc9EWL', icon: 'T', color: 'text-red-400' },
    { network: 'Polygon (MATIC)', address: '0xb745dc81c09bb00cc2faec09aa9a68dd131bace2', icon: 'P', color: 'text-purple-400' },
  ];

  const tokenomics = [
    { label: 'Public Sale', percentage: 40, color: 'bg-[#D4AF37]' },
    { label: 'Ecosystem & Rewards', percentage: 25, color: 'bg-blue-500' },
    { label: 'Team & Advisors', percentage: 15, color: 'bg-purple-500' },
    { label: 'Treasury Reserve', percentage: 12, color: 'bg-green-500' },
    { label: 'Liquidity Pool', percentage: 8, color: 'bg-orange-500' },
  ];

  const copyAddress = (address, index) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(index);
    toast.success('Address copied to clipboard!');
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  const calculateTokens = (amount) => {
    if (!amount || isNaN(amount)) return 0;
    return Math.floor(parseFloat(amount) / TOKEN_PRICE);
  };

  const handleConnectWallet = async (type) => {
    try {
      await connectWallet(type);
      toast.success('Wallet connected successfully!');
    } catch (err) {
      toast.error(err.message || 'Failed to connect wallet');
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-12 px-6" data-testid="token-offering-page">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 glass-panel px-4 py-2 mb-6">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="text-green-400 text-sm font-medium">ICO Live Now</span>
          </div>
          <h1 className="font-unbounded text-4xl sm:text-5xl font-bold text-white mb-4">
            AIH Token <span className="gradient-text">Offering</span>
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            Secure your position in the All In Hand ecosystem. Bitcoin-backed, community-driven, future-focused.
          </p>
        </div>

        {/* Countdown Timer */}
        <div className="glass-panel p-6 mb-8" data-testid="countdown-timer">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Clock className="text-[#D4AF37]" size={20} />
            <span className="text-white/80">Token Sale Ends In</span>
          </div>
          <div className="grid grid-cols-4 gap-4 max-w-md mx-auto">
            <div className="text-center">
              <div className="font-unbounded text-3xl font-bold text-white">{timeLeft.days}</div>
              <div className="text-xs text-white/60">Days</div>
            </div>
            <div className="text-center">
              <div className="font-unbounded text-3xl font-bold text-white">{timeLeft.hours}</div>
              <div className="text-xs text-white/60">Hours</div>
            </div>
            <div className="text-center">
              <div className="font-unbounded text-3xl font-bold text-white">{timeLeft.minutes}</div>
              <div className="text-xs text-white/60">Minutes</div>
            </div>
            <div className="text-center">
              <div className="font-unbounded text-3xl font-bold text-white">{timeLeft.seconds}</div>
              <div className="text-xs text-white/60">Seconds</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Buy Widget */}
          <div className="lg:col-span-2 space-y-6">
            {/* Sale Progress */}
            <div className="glass-panel p-6" data-testid="sale-progress">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-unbounded text-xl font-bold text-white">Sale Progress</h3>
                <span className="text-[#D4AF37] font-bold">{progress.toFixed(1)}%</span>
              </div>
              <Progress value={progress} className="h-4 mb-4" />
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-white">{(saleStats.tokensSold / 1000000).toFixed(1)}M</p>
                  <p className="text-xs text-white/60">Tokens Sold</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#D4AF37]">${(saleStats.raisedUSD / 1000000).toFixed(1)}M</p>
                  <p className="text-xs text-white/60">USD Raised</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{saleStats.participants.toLocaleString()}</p>
                  <p className="text-xs text-white/60">Participants</p>
                </div>
              </div>
            </div>

            {/* Purchase Widget */}
            <div className="glass-panel p-6" data-testid="purchase-widget">
              <h3 className="font-unbounded text-xl font-bold text-white mb-6">Purchase AIH Tokens</h3>
              
              {/* Wallet Connection */}
              {!isConnected ? (
                <div className="space-y-4">
                  <p className="text-white/70 text-center mb-4">Connect your wallet to participate in the token sale</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Button 
                      onClick={() => handleConnectWallet('unisat')}
                      className="btn-primary flex items-center justify-center gap-2"
                      data-testid="connect-unisat"
                    >
                      <span className="text-lg">₿</span> Connect Unisat (BTC)
                    </Button>
                    <Button 
                      onClick={() => handleConnectWallet('metamask')}
                      className="btn-secondary flex items-center justify-center gap-2"
                      data-testid="connect-metamask"
                    >
                      <Wallet size={18} /> Connect MetaMask (ETH)
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Connected Wallet Info */}
                  <div className="bg-[#0A1628]/80 rounded-xl p-4 border border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white/60 text-sm">Connected Wallet</span>
                      <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded">
                        {walletType === 'unisat' ? 'Bitcoin' : getNetworkName(chainId)}
                      </span>
                    </div>
                    <p className="font-mono text-sm text-white break-all">{account}</p>
                    <p className="text-[#D4AF37] mt-2">
                      Balance: {parseFloat(balance).toFixed(6)} {walletType === 'unisat' ? 'BTC' : 'ETH'}
                    </p>
                  </div>

                  {/* Purchase Form */}
                  <div className="space-y-4">
                    <div>
                      <label className="text-white/80 text-sm mb-2 block">Amount (USD)</label>
                      <Input
                        type="number"
                        placeholder="Enter amount in USD"
                        value={purchaseAmount}
                        onChange={(e) => setPurchaseAmount(e.target.value)}
                        className="input-cosmic"
                        data-testid="purchase-amount-input"
                      />
                    </div>
                    
                    <div className="bg-[#0A1628]/60 rounded-xl p-4 border border-[#D4AF37]/30">
                      <div className="flex justify-between mb-2">
                        <span className="text-white/60">Token Price</span>
                        <span className="text-white font-bold">${TOKEN_PRICE}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">You will receive</span>
                        <span className="text-[#D4AF37] font-bold">
                          {calculateTokens(purchaseAmount).toLocaleString()} AIH
                        </span>
                      </div>
                    </div>

                    <Button className="w-full btn-primary text-lg py-6" data-testid="buy-tokens-btn">
                      <Coins className="mr-2" size={20} />
                      Buy AIH Tokens
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Deposit Addresses */}
            <div className="glass-panel p-6" data-testid="deposit-addresses">
              <h3 className="font-unbounded text-xl font-bold text-white mb-2">Deposit Addresses</h3>
              <p className="text-white/60 text-sm mb-6">
                Send crypto to these addresses. You'll receive AIH tokens at the current rate.
              </p>
              
              <div className="space-y-4">
                {depositAddresses.map((addr, i) => (
                  <div key={i} className="bg-[#0A1628]/80 rounded-xl p-4 border border-white/5 hover:border-white/20 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-xl ${addr.color}`}>{addr.icon}</span>
                        <span className="font-semibold text-white">{addr.network}</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => copyAddress(addr.address, i)}
                        className="text-white/60 hover:text-white"
                      >
                        {copiedAddress === i ? (
                          <CheckCircle size={16} className="text-green-400" />
                        ) : (
                          <Copy size={16} />
                        )}
                      </Button>
                    </div>
                    <p className="font-mono text-sm text-white/80 break-all">{addr.address}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Token Info */}
          <div className="space-y-6">
            {/* Token Details Card */}
            <div className="glass-panel p-6" data-testid="token-details">
              <h3 className="font-unbounded text-xl font-bold text-white mb-6">Token Details</h3>
              <div className="space-y-4">
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="text-white/60">Token Name</span>
                  <span className="text-white font-bold">AIH Token</span>
                </div>
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="text-white/60">Symbol</span>
                  <span className="text-white font-bold">AIH</span>
                </div>
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="text-white/60">Price</span>
                  <span className="text-[#D4AF37] font-bold">${TOKEN_PRICE}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="text-white/60">Total Supply</span>
                  <span className="text-white font-bold">{(TOTAL_SUPPLY / 1000000)}M</span>
                </div>
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="text-white/60">Available</span>
                  <span className="text-white font-bold">{(tokensRemaining / 1000000).toFixed(1)}M</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-white/60">Backing</span>
                  <span className="text-[#F7931A] font-bold">Bitcoin</span>
                </div>
              </div>
            </div>

            {/* Tokenomics Chart */}
            <div className="glass-panel p-6" data-testid="tokenomics">
              <h3 className="font-unbounded text-xl font-bold text-white mb-6">Tokenomics</h3>
              <div className="space-y-3">
                {tokenomics.map((item, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white/80">{item.label}</span>
                      <span className="text-white font-bold">{item.percentage}%</span>
                    </div>
                    <div className="h-2 bg-[#0A1628] rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${item.color} rounded-full`} 
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Trust Badges */}
            <div className="glass-panel p-6" data-testid="trust-section">
              <h3 className="font-unbounded text-lg font-bold text-white mb-4">Why AIH?</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#F7931A]/20 flex items-center justify-center">
                    <Shield className="text-[#F7931A]" size={20} />
                  </div>
                  <div>
                    <p className="text-white font-medium">Bitcoin Backed</p>
                    <p className="text-xs text-white/60">Secure treasury reserves</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <Lock className="text-green-400" size={20} />
                  </div>
                  <div>
                    <p className="text-white font-medium">Audited Smart Contract</p>
                    <p className="text-xs text-white/60">Security verified</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Users className="text-blue-400" size={20} />
                  </div>
                  <div>
                    <p className="text-white font-medium">Community Governed</p>
                    <p className="text-xs text-white/60">DAO voting rights</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Zap className="text-purple-400" size={20} />
                  </div>
                  <div>
                    <p className="text-white font-medium">Staking Rewards</p>
                    <p className="text-xs text-white/60">Up to 25% APY</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenOffering;
